import React, { useState, useEffect } from 'react';
import Button from '../components/common/Button';
import ParticleCanvas from '../components/common/ParticleCanvas';
import { 
  Zap, 
  ArrowRight, 
  ChevronRight 
} from 'lucide-react';

const HEADLINE_WORDS = [
  "MT5 Copy Trading",
  "Algo Trade Execution",
];

const TypewriterHeadline = () => {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [blink, setBlink] = useState(true);

  // Blinking cursor toggle
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setBlink((prev) => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  // Fail-safe Typewriter Effect Loop
  useEffect(() => {
    const currentWord = HEADLINE_WORDS[wordIndex];
    let timeoutId;

    if (!isDeleting) {
      if (text.length < currentWord.length) {
        // Type next character slowly (130ms)
        timeoutId = setTimeout(() => {
          setText(currentWord.slice(0, text.length + 1));
        }, 130);
      } else {
        // Full word typed out -> Hold for 3.5 seconds, then start deleting
        timeoutId = setTimeout(() => {
          setIsDeleting(true);
        }, 3500);
      }
    } else {
      if (text.length > 0) {
        // Delete character smoothly (70ms)
        timeoutId = setTimeout(() => {
          setText(currentWord.slice(0, text.length - 1));
        }, 70);
      } else {
        // Full word deleted -> Switch to next word and start typing
        setIsDeleting(false);
        setWordIndex((prevIndex) => (prevIndex + 1) % HEADLINE_WORDS.length);
      }
    }

    return () => clearTimeout(timeoutId);
  }, [text, isDeleting, wordIndex]);

  return (
    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#FFFFFF] leading-tight sm:leading-tight min-h-[3.5rem] sm:min-h-[4.5rem]">
      Automated{" "}
      <span className="text-[#F97316] relative inline-block">
        {text}
        <span className={`ml-0.5 text-[#F97316] font-normal ${blink ? 'opacity-100' : 'opacity-0'} transition-opacity`}>|</span>
      </span>
    </h1>
  );
};

const LandingPage = ({ onGoToLogin, onGoToRegister }) => {
  return (
    <div className="relative min-h-screen bg-[#09090B] text-[#FFFFFF] font-sans antialiased flex flex-col justify-between selection:bg-[#F97316] selection:text-white overflow-hidden">
      {/* Interactive Infinite Canvas Background */}
      <ParticleCanvas />
      
      {/* 1. Public Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#09090B]/90 backdrop-blur-md border-b border-[#27272A] h-16 px-4 sm:px-8 flex items-center justify-between">
        {/* Brand Logo */}
        <div className="flex items-center space-x-3">
          <img 
            src="/logo.png" 
            alt="DUBAI GOLD X Logo" 
            className="w-10 h-10 object-contain rounded-full shrink-0" 
          />
          <div>
            <h1 className="text-sm font-extrabold text-[#FFFFFF] tracking-wider uppercase leading-none">DUBAI GOLD X</h1>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">MT5 Copy Trading & Profit Sharing</p>
          </div>
        </div>

        {/* Quick Action Navigation Buttons */}
        <div className="flex items-center space-x-3">
          <button 
            onClick={onGoToLogin}
            className="px-4 py-2 rounded-xl text-xs font-bold text-[#FFFFFF] bg-[#121215] border border-[#27272A] hover:bg-[#1A1A1E] transition-all"
          >
            Sign In
          </button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={onGoToRegister}
            className="flex items-center space-x-1.5 shadow-[0_2px_15px_rgba(249,115,22,0.35)]"
          >
            <span>Get Started</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </header>

      {/* 2. Hero Section (Centered Vertically in Flex Container) */}
      <main className="flex-1 pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center flex flex-col items-center justify-center space-y-8 w-full">
        
        {/* Top Status Pill */}
        <div className="inline-flex items-center space-x-2 bg-[#121215] border border-[#F59E0B]/30 px-4 py-2 rounded-full text-xs font-bold text-[#F59E0B] shadow-card">
          <Zap className="w-3.5 h-3.5 text-[#F97316] animate-pulse" />
          <span>Automated Trade Copier</span>
        </div>

        {/* Main Headline with Typewriter Animation */}
        <div className="max-w-4xl mx-auto space-y-4">
          <TypewriterHeadline />
          <p className="text-sm sm:text-base text-[#9CA3AF] max-w-2xl mx-auto leading-relaxed">
            Connect your MetaTrader 5 account to our automated trade copier. Enjoy seamless periodic PnL synchronization, transparent USDT wallet management, and earn up to 5 levels of downline referral commissions.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2 w-full sm:w-auto">
          <Button 
            variant="primary" 
            size="lg" 
            onClick={onGoToRegister}
            className="w-full sm:w-auto px-8 py-3.5 text-sm font-extrabold shadow-[0_4px_25px_rgba(249,115,22,0.4)] flex items-center justify-center space-x-2"
          >
            <span>Create Free Account</span>
            <ArrowRight className="w-4 h-4" />
          </Button>

          <button
            onClick={onGoToLogin}
            className="w-full sm:w-auto px-8 py-3.5 rounded-xl text-sm font-extrabold text-[#FFFFFF] bg-[#121215] border border-[#27272A] hover:bg-[#1A1A1E] transition-all flex items-center justify-center space-x-2"
          >
            <span>Access Client Portal</span>
            <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
          </button>
        </div>

      </main>

      {/* 3. Footer (Always Positioned at Very Bottom of Viewport) */}
      <footer className="py-6 px-4 sm:px-6 lg:px-8 border-t border-[#27272A] bg-[#09090B] w-full shrink-0">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#9CA3AF]">
          <div className="flex items-center space-x-2">
            <img 
              src="/logo.png" 
              alt="DUBAI GOLD X Logo" 
              className="w-7 h-7 object-contain rounded-full shrink-0" 
            />
            <span className="font-bold text-[#FFFFFF]">DUBAI GOLD X</span>
            <span>© {new Date().getFullYear()} All rights reserved.</span>
          </div>

          <div className="flex items-center space-x-6">
            <button onClick={onGoToLogin} className="hover:text-[#FFFFFF] transition-colors">Sign In</button>
            <button onClick={onGoToRegister} className="hover:text-[#FFFFFF] transition-colors">Register</button>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
